import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Switch, Button, message, Upload, Space } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_CSC_API_BASE_URL;

const CategoryModal = ({ open, onClose, editData, serviceGroups }) => {
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const iconValue = Form.useWatch("icon", form);

  // Find the group object by id or name
  const getGroupByIdOrName = (id, name) => {
    if (id) {
      return serviceGroups.find((g) => String(g.service_group_id) === String(id));
    }
    if (name) {
      return serviceGroups.find((g) => g.name === name);
    }
    return undefined;
  };

  useEffect(() => {
    if (editData) {
      // Find the group object for the dropdown
      const groupObj = getGroupByIdOrName(editData.service_group_id, editData.service_group_name);
      form.setFieldsValue({
        display_name: editData.display_name,
        slug: editData.slug,
        icon: editData.icon,
        biller_category: editData.biller_category,
        service_group_id: groupObj ? groupObj.service_group_id : undefined,
        productId: editData.productId,
        productName: editData.productName,
        is_published: !!editData.is_published,
      });
    } else {
      form.resetFields();
    }
  }, [editData, form, open, serviceGroups]);

  const handleUpload = async ({ file, onSuccess, onError }) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("icon", file);
      const res = await axios.post(`${BASE_URL}icons/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const url = res?.data?.payload;
      if (url) {
        form.setFieldsValue({ icon: url });
        message.success("Image uploaded successfully!");
        onSuccess(res.data);
      } else {
        throw new Error("Invalid upload response");
      }
    } catch (err) {
      console.error("Upload error:", err);
      const errorMsg =
        err?.response?.status === 500
          ? "Backend error (500): GCP Storage key / Bucket configuration issue on server. You can enter Icon URL manually."
          : err?.response?.data?.message || "Failed to upload image";
      message.error(errorMsg);
      onError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      // Find the selected group object
      const groupObj = serviceGroups.find(
        (g) => String(g.service_group_id) === String(values.service_group_id)
      );
      const payload = {
        display_name: values.display_name,
        slug: values.slug,
        icon: values.icon,
        biller_category: values.biller_category,
        service_group_id: values.service_group_id,
        service_group_name: groupObj ? groupObj.name : "",
        productId: values.productId,
        productName: values.productName,
        is_published: !!values.is_published,
        updated_by: "kamaljeet@plutos.one",
        created_at: editData?.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_deleted: false,
        priority: "1",
        service_id: editData?.service_id,
      };
      if (editData) {
        await axios.put(
          `${BASE_URL}categories/csc-dashboard/${editData.service_id}`,
          payload
        );
        message.success("Category updated!");
      } else {
        await axios.post(
          `${BASE_URL}categories/csc-dashboard`,
          payload
        );
        message.success("Category added!");
      }
      onClose(true);
    } catch (err) {
      message.error("Failed to save category");
    }
  };

  return (
    <Modal
      open={open}
      title={editData ? "Edit Category" : "Add Category"}
      onCancel={() => onClose(false)}
      onOk={handleSubmit}
      okText={editData ? "Update" : "Add"}
      destroyOnClose
      width={600}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Display Name"
          name="display_name"
          rules={[{ required: true, message: "Display Name is required" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Slug"
          name="slug"
          rules={[{ required: true, message: "Slug is required" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Icon URL" style={{ marginBottom: 16 }}>
          <Form.Item
            name="icon"
            rules={[{ required: false }]}
            style={{ marginBottom: 8 }}
          >
            <Input placeholder="Enter Icon URL or upload image" />
          </Form.Item>
          <Space align="center">
            <Upload
              customRequest={handleUpload}
              showUploadList={false}
              accept="image/*,.svg"
            >
              <Button icon={<UploadOutlined />} loading={uploading}>
                Upload Image
              </Button>
            </Upload>
            {iconValue && (
              <img
                src={iconValue}
                alt="Preview"
                style={{ width: 32, height: 32, objectFit: "contain", borderRadius: 4, border: "1px solid #d9d9d9" }}
              />
            )}
          </Space>
        </Form.Item>
        <Form.Item
          label="Biller Category"
          name="biller_category"
          rules={[{ required: true, message: "Biller Category is required" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Service Group"
          name="service_group_id"
          rules={[{ required: true, message: "Service Group is required" }]}
        >
          <Select
            placeholder="Select Service Group"
            options={serviceGroups.map((g) => ({
              label: g.name,
              value: g.service_group_id,
            }))}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item
          label="Product ID"
          name="productId"
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Product Name"
          name="productName"
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Region Requirement"
          name="is_region_required"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Button type="primary" htmlType="submit" onClick={handleSubmit} style={{ display: "none" }}>
          Submit
        </Button>
      </Form>
    </Modal>
  );
};

export default CategoryModal;